import { useEffect, useRef, useState } from 'react';
import EditorJS, { OutputData } from '@editorjs/editorjs';
import Header from '@editorjs/header';
import List from '@editorjs/list';
import { PreservingParagraph } from './PreservingParagraph';
import Image from '@editorjs/image';
import Quote from '@editorjs/quote';
// @ts-ignore
import Embed from '@editorjs/embed';
import { ArtistFavoritesBlock } from './ArtistFavoritesBlock';
import { CustomCodeTool } from './CustomCodeTool';
import { CustomLinkTool } from './CustomLinkTool';
import { ContentPlanBlock } from './ContentPlanBlock';
import { TikTokPreviewTool } from './TikTokPreviewTool';
import { TikTokEmbedBlock } from './TikTokEmbedBlock';
import { KanbanBlock } from './KanbanBlock';
import { LibraryBlock } from './LibraryBlock';
import { enableSlashCommands } from './SlashCommandPlugin';
// @ts-ignore
import Undo from 'editorjs-undo';

interface EditorJSWrapperProps {
  data?: OutputData;
  onChange?: (data: OutputData) => void;
  placeholder?: string;
  readOnly?: boolean;
  artistId?: string;
  releaseDate?: string;
}

export const EditorJSWrapper = ({ 
  data, 
  onChange, 
  placeholder = 'Start typing...',
  readOnly = false,
  artistId,
  releaseDate
}: EditorJSWrapperProps) => {
  const editorRef = useRef<EditorJS | null>(null);
  const holderRef = useRef<HTMLDivElement>(null);
  const isInitialized = useRef(false);
  const saveTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  
  // Social embed modal state
  const [showSocialModal, setShowSocialModal] = useState(false);
  const [socialEmbedUrl, setSocialEmbedUrl] = useState<string>('');
  const [isIframeLoaded, setIsIframeLoaded] = useState(false);

  useEffect(() => {
    console.log('useEffect called for EditorJS initialization');
    if (!holderRef.current || isInitialized.current || editorRef.current) return;

    isInitialized.current = true;

    // Initialize Editor.js
    const editor = new EditorJS({
      holder: holderRef.current,
      placeholder,
      readOnly,
      data: data || {
        blocks: []
      },
      defaultBlock: 'paragraph',
      tools: {
        header: {
          class: Header as any,
          inlineToolbar: true,
          config: {
            levels: [1, 2, 3],
            defaultLevel: 2
          }
        },
        list: {
          class: List as any,
          inlineToolbar: true,
        },
        paragraph: {
          class: PreservingParagraph as any,
          inlineToolbar: true,
        },
        image: {
          class: Image,
          config: {
            uploader: {
              uploadByFile(file: File) {
                // TODO: Implement image upload to Supabase
                return Promise.resolve({
                  success: 1,
                  file: {
                    url: URL.createObjectURL(file),
                  }
                });
              },
              uploadByUrl(url: string) {
                return Promise.resolve({
                  success: 1,
                  file: {
                    url: url,
                  }
                });
              }
            }
          }
        },
        linkTool: {
          class: CustomLinkTool as any,
          config: {
            endpoint: 'http://localhost:8008/fetchUrl', // Your backend endpoint for url data fetching
          }
        },
        quote: {
          class: Quote as any,
          inlineToolbar: true,
        },
        code: {
          class: CustomCodeTool as any,
        },
        embed: {
          class: Embed,
          config: {
            services: {
              youtube: true,
              twitter: true,
              instagram: false,
              tiktok: true,
            }
          }
        },
        artistFavorites: {
          class: ArtistFavoritesBlock as any,
          config: {
            artistId: artistId || '',
          }
        },
        contentPlan: {
          class: ContentPlanBlock as any,
          config: {
            artistId: artistId || '',
            releaseDate: releaseDate || '',
          }
        },
        tiktokEmbed: {
          class: TikTokEmbedBlock as any,
        },
        tiktokPreview: {
          class: TikTokPreviewTool as any,
        },
        kanban: {
          class: KanbanBlock as any,
        },
        library: {
          class: LibraryBlock as any,
          config: {
            artistId: artistId || '',
          }
        },
      },
      onChange: async () => {
        if (onChange && editorRef.current) {
          // Debounce to prevent rapid-fire saves creating extra blank lines
          if (saveTimerRef.current) {
            clearTimeout(saveTimerRef.current);
          }
          saveTimerRef.current = setTimeout(async () => {
            try {
              if (editorRef.current) {
                await editorRef.current.isReady;
                const outputData = await editorRef.current.save();
                onChange(outputData);
              }
            } catch (error) {
              console.error('Error saving editor data:', error);
            }
          }, 300);
        }
      },
      onReady: () => {
        console.log('Editor.js is ready');
        // Enable slash commands
        if (holderRef.current) {
          enableSlashCommands(editor, holderRef.current);
        }
        // Initialize undo/redo
        new Undo({ editor });

        // Enable drag-selection outside parent container by forwarding mouse events
        console.log('Setting up event forwarding in onReady...');
        if (holderRef.current) {
          const wrapper = holderRef.current.closest('.editorjs-wrapper');
          console.log('Wrapper found:', !!wrapper);
          if (wrapper) {
            const redactor = holderRef.current.querySelector('.codex-editor__redactor');
            console.log('Redactor found:', !!redactor);
            if (redactor) {
              let isDragging = false;

              wrapper.addEventListener('mousedown', (e: Event) => {
                const mouseEvent = e as MouseEvent;
                const target = mouseEvent.target as HTMLElement;
                // Don't interfere with interactive elements or events from redactor
                if (target.closest('button, a, input, textarea, select, .ce-toolbar, .codex-editor__redactor')) return;

                isDragging = true;

                // Forward mousedown to redactor
                const rect = redactor.getBoundingClientRect();
                const forwardedEvent = new MouseEvent('mousedown', {
                  bubbles: true,
                  cancelable: true,
                  clientX: Math.max(rect.left, Math.min(rect.right, mouseEvent.clientX)),
                  clientY: Math.max(rect.top, Math.min(rect.bottom, mouseEvent.clientY)),
                  button: mouseEvent.button,
                  buttons: mouseEvent.buttons
                });
                redactor.dispatchEvent(forwardedEvent);
              });

              wrapper.addEventListener('mousemove', (e: Event) => {
                if (!isDragging) return;
                const mouseEvent = e as MouseEvent;
                const target = mouseEvent.target as HTMLElement;
                // Don't process events from redactor
                if (target.closest('.codex-editor__redactor')) return;

                // Forward mousemove to redactor
                const rect = redactor.getBoundingClientRect();
                const forwardedEvent = new MouseEvent('mousemove', {
                  bubbles: true,
                  cancelable: true,
                  clientX: Math.max(rect.left, Math.min(rect.right, mouseEvent.clientX)),
                  clientY: Math.max(rect.top, Math.min(rect.bottom, mouseEvent.clientY)),
                  button: mouseEvent.button,
                  buttons: mouseEvent.buttons
                });
                redactor.dispatchEvent(forwardedEvent);
              });

              wrapper.addEventListener('mouseup', (e: Event) => {
                if (!isDragging) return;
                isDragging = false;
                const mouseEvent = e as MouseEvent;
                const target = mouseEvent.target as HTMLElement;
                // Don't process events from redactor
                if (target.closest('.codex-editor__redactor')) return;

                // Forward mouseup to redactor
                const rect = redactor.getBoundingClientRect();
                const forwardedEvent = new MouseEvent('mouseup', {
                  bubbles: true,
                  cancelable: true,
                  clientX: Math.max(rect.left, Math.min(rect.right, mouseEvent.clientX)),
                  clientY: Math.max(rect.top, Math.min(rect.bottom, mouseEvent.clientY)),
                  button: mouseEvent.button,
                  buttons: mouseEvent.buttons
                });
                redactor.dispatchEvent(forwardedEvent);
              });

              // Change text color when blocks are selected
              const updateSelectedBlockTextColor = () => {
                if (!holderRef.current) return;

                const selectedBlocks = holderRef.current?.querySelectorAll('.ce-block--selected');

                selectedBlocks?.forEach((block: Element) => {
                  const textElements = block.querySelectorAll('.ce-paragraph, .ce-header, .cdx-list, .ce-inline-code, .ce-auto-link, a');
                  textElements.forEach((el: Element) => {
                    (el as HTMLElement).style.setProperty('color', 'rgb(107, 114, 128)', 'important');
                  });
                });

                // Reset color for non-selected blocks
                const allBlocks = holderRef.current?.querySelectorAll('.ce-block');
                allBlocks?.forEach((block: Element) => {
                  if (!block.classList.contains('ce-block--selected')) {
                    const textElements = block.querySelectorAll('.ce-paragraph, .ce-header, .cdx-list, .ce-inline-code, .ce-auto-link, a');
                    textElements.forEach((el: Element) => {
                      (el as HTMLElement).style.removeProperty('color');
                    });
                  }
                });
              };

              // Use setInterval for more reliable updates (500ms to reduce spam)
              const intervalId = setInterval(updateSelectedBlockTextColor, 500);

              // Run initially
              updateSelectedBlockTextColor();

              // Store intervalId for cleanup
              (holderRef.current as any)._textColorInterval = intervalId;
            }
          }
        }

        // Open links in new tab or modal on click (contenteditable blocks clicks)
        if (holderRef.current) {
          holderRef.current.addEventListener('click', (e: MouseEvent) => {
            const link = (e.target as HTMLElement).closest('a');
            if (link && link.href) {
              e.preventDefault();
              
              // Check if it's Instagram or TikTok link
              let embedUrl = '';
              
              // Instagram links
              if (link.href.includes('instagram.com')) {
                const url = link.href.split('?')[0]; // Strip query parameters
                if (url.includes('/reel/') || url.includes('/reels/')) {
                  const normalizedUrl = url.replace('/reels/', '/reel/');
                  embedUrl = normalizedUrl + 'embed/';
                } else if (url.includes('/p/')) {
                  embedUrl = url + 'embed/';
                }
              }
              // TikTok links
              else if (link.href.includes('tiktok.com')) {
                const url = link.href.split('?')[0]; // Strip query parameters
                if (url.includes('/video/')) {
                  embedUrl = url.replace('/video/', '/embed/');
                } else if (url.includes('/photo/')) {
                  embedUrl = url.replace('/photo/', '/embed/');
                }
              }
              
              if (embedUrl) {
                // Open modal for Instagram/TikTok embeds
                setSocialEmbedUrl(embedUrl);
                setShowSocialModal(true);
                setIsIframeLoaded(false);
              } else {
                // Open other links in new tab
                window.open(link.href, '_blank', 'noopener,noreferrer');
              }
            }
          });

          // Auto-linkify URLs when a paragraph block loses focus
          holderRef.current.addEventListener('focusout', (e: FocusEvent) => {
            const target = e.target as HTMLElement;
            if (!target?.classList?.contains('ce-paragraph')) return;
            const html = target.innerHTML || '';
            const urlRegex = /(?<!=["'])(https?:\/\/[^\s<>"']+)/;
            if (!urlRegex.test(html) || html.includes('ce-auto-link')) return;
            target.innerHTML = html.replace(
              /(<a[^>]*>.*?<\/a>)|((https?:\/\/[^\s<>"']+))/gi,
              (_match: string, existingLink: string, plainUrl: string) => {
                if (existingLink) return existingLink;
                
                // Check if it's Instagram or TikTok
                const isInstagram = plainUrl.includes('instagram.com');
                const isTikTok = plainUrl.includes('tiktok.com');
                
                if (isInstagram || isTikTok) {
                  // Create wrapper with description and note fields
                  return `
                    <div class="social-link-wrapper" data-url="${plainUrl}">
                      <a href="${plainUrl}" target="_blank" rel="noopener noreferrer" class="ce-auto-link social-link">${plainUrl}</a>
                      <div class="social-link-field social-link-description" style="display: none;">
                        <input type="text" placeholder="Description" class="social-link-input" data-field="description" />
                      </div>
                      <div class="social-link-field social-link-note" style="display: none;">
                        <input type="text" placeholder="Note" class="social-link-input" data-field="note" />
                      </div>
                    </div>
                  `;
                }
                
                return `<a href="${plainUrl}" target="_blank" rel="noopener noreferrer" class="ce-auto-link">${plainUrl}</a>`;
              }
            );
            
            // Add hover listeners for social links
            const socialLinks = target.querySelectorAll('.social-link');
            socialLinks.forEach((link) => {
              const wrapper = link.closest('.social-link-wrapper');
              if (!wrapper) return;
              
              const descriptionField = wrapper.querySelector('.social-link-description') as HTMLElement;
              const noteField = wrapper.querySelector('.social-link-note') as HTMLElement;
              const descriptionInput = wrapper.querySelector('[data-field="description"]') as HTMLInputElement;
              const noteInput = wrapper.querySelector('[data-field="note"]') as HTMLInputElement;
              
              link.addEventListener('mouseenter', () => {
                // Show fields on hover if they're empty
                if (descriptionField && !descriptionInput?.value) {
                  descriptionField.style.display = 'block';
                }
                if (noteField && !noteInput?.value) {
                  noteField.style.display = 'block';
                }
              });
              
              link.addEventListener('mouseleave', () => {
                // Hide fields on mouse leave if they're empty
                if (descriptionField && !descriptionInput?.value) {
                  descriptionField.style.display = 'none';
                }
                if (noteField && !noteInput?.value) {
                  noteField.style.display = 'none';
                }
              });
              
              // Show field permanently when user types in it
              descriptionInput?.addEventListener('input', (e) => {
                const target = e.target as HTMLInputElement;
                if (descriptionField) {
                  descriptionField.style.display = target.value ? 'block' : 'none';
                }
              });
              
              noteInput?.addEventListener('input', (e) => {
                const target = e.target as HTMLInputElement;
                if (noteField) {
                  noteField.style.display = target.value ? 'block' : 'none';
                }
              });
            });
          });
        }
      },
    });

    editorRef.current = editor;

    return () => {
      // Clear text color interval
      if (holderRef.current && (holderRef.current as any)._textColorInterval) {
        clearInterval((holderRef.current as any)._textColorInterval);
      }
      if (editorRef.current && editorRef.current.destroy) {
        editorRef.current.destroy();
        isInitialized.current = false;
      }
    };
  }, []); // Only initialize once

  return (
    <div className="editorjs-wrapper w-full">
      <style>{`
        .codex-editor {
          color: white;
        }
        .codex-editor__redactor {
          padding-bottom: 16rem;
        }
        .ce-block__content,
        .ce-toolbar__content {
          max-width: none;
        }
        .ce-paragraph {
          color: rgb(229, 231, 235);
          line-height: 1.75;
        }
        .ce-header {
          font-weight: bold;
          color: white;
        }
        .ce-header[data-level="2"],
        h2.ce-header {
          font-size: 1.875rem !important;
          line-height: 2.25rem !important;
          margin-bottom: 1.5rem !important;
          padding-bottom: 0.75rem !important;
          margin-top: 1.5rem !important;
          font-weight: 700 !important;
          letter-spacing: -0.01em !important;
        }
        .ce-header[data-level="1"] {
          font-size: 3.5rem !important;
          line-height: 4rem !important;
          margin-bottom: 2rem !important;
        }
        .ce-header[data-level="3"] {
          font-size: 2rem !important;
          line-height: 2.5rem !important;
          margin-bottom: 1rem !important;
        }
        .cdx-list {
          color: rgb(229, 231, 235);
        }
        .ce-code__textarea {
          background-color: rgb(31, 41, 55);
          color: rgb(229, 231, 235);
          font-family: monospace;
        }
        .cdx-quote {
          border-left: 4px solid rgb(59, 130, 246);
          padding-left: 1rem;
          font-style: italic;
          color: rgb(209, 213, 219);
        }
        .ce-inline-code {
          background-color: rgb(31, 41, 55);
          color: rgb(96, 165, 250);
          padding: 0 0.25rem;
          border-radius: 0.25rem;
        }
        /* Hide settings button completely */
        .ce-toolbar__settings-btn,
        .ce-settings {
          display: none !important;
        }
        
        /* Show plus button on hover */
        .ce-toolbar__plus {
          opacity: 0;
          transition: opacity 0.2s;
          color: rgb(156, 163, 175);
        }
        .ce-block:hover .ce-toolbar__plus {
          opacity: 1;
        }
        .ce-toolbar__plus:hover {
          color: white;
        }
        .ce-inline-toolbar,
        .ce-conversion-toolbar,
        .ce-settings,
        .ce-popover {
          background-color: black !important;
          border: 1px solid black !important;
          border-radius: 0.5rem !important;
          box-shadow: 0 10px 15px -3px rgba(0, 0, 0, 0.5) !important;
          z-index: 9999 !important;
        }
        .ce-inline-toolbar {
          display: flex !important;
          align-items: center !important;
          padding: 4px !important;
        }
        .ce-inline-toolbar__buttons {
          display: flex !important;
          gap: 2px !important;
        }
        .ce-popover__container {
          background-color: rgb(31, 41, 55) !important;
        }
        .ce-inline-tool,
        .ce-conversion-tool,
        .ce-settings__button,
        .ce-popover-item {
          color: white !important;
          cursor: pointer !important;
          padding: 6px 8px !important;
          border-radius: 4px !important;
          transition: background-color 0.15s ease !important;
        }
        .ce-inline-tool:hover,
        .ce-conversion-tool:hover,
        .ce-settings__button:hover,
        .ce-popover-item:hover {
          color: white !important;
          background-color: rgb(55, 65, 81) !important;
        }
        .ce-inline-tool--active {
          background-color: rgb(59, 130, 246) !important;
          color: white !important;
        }
        .ce-inline-tool svg,
        .ce-conversion-tool svg {
          fill: currentColor !important;
          stroke: currentColor !important;
        }
        /* Replace italic icon with Font Awesome icon */
        .ce-inline-tool--italic svg {
          display: none;
        }
        .ce-inline-tool--italic::before {
          content: '';
          display: inline-block;
          width: 20px;
          height: 20px;
          background-image: url('data:image/svg+xml,<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 640 640" fill="white"><path d="M256 128C256 110.3 270.3 96 288 96L480 96C497.7 96 512 110.3 512 128C512 145.7 497.7 160 480 160L421.3 160L288 480L352 480C369.7 480 384 494.3 384 512C384 529.7 369.7 544 352 544L160 544C142.3 544 128 529.7 128 512C128 494.3 142.3 480 160 480L218.7 480L352 160L288 160C270.3 160 256 145.7 256 128z"/></svg>');
          background-size: contain;
          background-repeat: no-repeat;
          background-position: center;
        }
        /* Replace link inline tool icon with Font Awesome icon */
        .ce-inline-tool--link svg {
          display: none;
        }
        .ce-inline-tool--link::before {
          content: '';
          display: inline-block;
          width: 20px;
          height: 20px;
          background-image: url('data:image/svg+xml,<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 640 640" fill="white"><path d="M451.5 160C434.9 160 418.8 164.5 404.7 172.7C388.9 156.7 370.5 143.3 350.2 133.2C378.4 109.2 414.3 96 451.5 96C537.9 96 608 166 608 252.5C608 294 591.5 333.8 562.2 363.1L491.1 434.2C461.8 463.5 422 480 380.5 480C294.1 480 224 410 224 323.5C224 322 224 320.5 224.1 319C224.6 301.3 239.3 287.4 257 287.9C274.7 288.4 288.6 303.1 288.1 320.8C288.1 321.7 288.1 322.6 288.1 323.4C288.1 374.5 329.5 415.9 380.6 415.9C405.1 415.9 428.6 406.2 446 388.8L517.1 317.7C534.4 300.4 544.2 276.8 544.2 252.3C544.2 201.2 502.8 159.8 451.7 159.8zM307.2 237.3C305.3 236.5 303.4 235.4 301.7 234.2C289.1 227.7 274.7 224 259.6 224C235.1 224 211.6 233.7 194.2 251.1L123.1 322.2C105.8 339.5 96 363.1 96 387.6C96 438.7 137.4 480.1 188.5 480.1C205 480.1 221.1 475.7 235.2 467.5C251 483.5 269.4 496.9 289.8 507C261.6 530.9 225.8 544.2 188.5 544.2C102.1 544.2 32 474.2 32 387.7C32 346.2 48.5 306.4 77.8 277.1L148.9 206C178.2 176.7 218 160.2 259.5 160.2C346.1 160.2 416 230.8 416 317.1C416 318.4 416 319.7 416 321C415.6 338.7 400.9 352.6 383.2 352.2C365.5 351.8 351.6 337.1 352 319.4C352 318.6 352 317.9 352 317.1C352 283.4 334 253.8 307.2 237.5z"/></svg>');
          background-size: contain;
          background-repeat: no-repeat;
          background-position: center;
        }
        /* Replace inline code tool icon with Font Awesome icon */
        .ce-inline-tool--code svg {
          display: none;
        }
        .ce-inline-tool--code::before {
          content: '';
          display: inline-block;
          width: 20px;
          height: 20px;
          background-image: url('data:image/svg+xml,<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 640 640" fill="white"><path d="M392.8 65.2C375.8 60.3 358.1 70.2 353.2 87.2L225.2 535.2C220.3 552.2 230.2 569.9 247.2 574.8C264.2 579.7 281.9 569.8 286.8 552.8L414.8 104.8C419.7 87.8 409.8 70.1 392.8 65.2zM457.4 201.3C444.9 213.8 444.9 234.1 457.4 246.6L530.8 320L457.4 393.4C444.9 405.9 444.9 426.2 457.4 438.7C469.9 451.2 490.2 451.2 502.7 438.7L598.7 342.7C611.2 330.2 611.2 309.9 598.7 297.4L502.7 201.4C490.2 188.9 469.9 188.9 457.4 201.4zM182.7 201.3C170.2 188.8 149.9 188.8 137.4 201.3L41.4 297.3C28.9 309.8 28.9 330.1 41.4 342.6L137.4 438.6C149.9 451.1 170.2 451.1 182.7 438.6C195.2 426.1 195.2 405.8 182.7 393.3L109.3 320L182.6 246.6C195.1 234.1 195.1 213.8 182.6 201.3z"/></svg>');
          background-size: contain;
          background-repeat: no-repeat;
          background-position: center;
        }
        .ce-popover-item__title {
          color: white !important;
        }
        .ce-popover-item__icon {
          color: rgb(156, 163, 175) !important;
        }
        .ce-popover-item:hover .ce-popover-item__icon {
          color: white !important;
        }
        .ce-popover__nothing-found-message {
          color: rgb(156, 163, 175) !important;
        }
        .ce-popover-item--active {
          background-color: rgb(55, 65, 81) !important;
        }
        .ce-popover-item--active .ce-popover-item__title,
        .ce-popover-item--active .ce-popover-item__icon {
          color: white !important;
        }
        .ce-popover-item-separator__line {
          background-color: rgb(55, 65, 81) !important;
        }
        .codex-editor--empty .ce-block:first-child .ce-paragraph[data-placeholder]:empty::before {
          color: rgb(107, 114, 128);
        }
        /* Text selection styling */
        .codex-editor ::selection {
          background-color: rgb(59, 130, 246) !important;
          color: white !important;
        }
        .codex-editor ::-moz-selection {
          background-color: rgb(59, 130, 246) !important;
          color: white !important;
        }
        /* Block selection styling - transparent to only show text selection */
        .codex-editor .ce-block--selected {
          background-color: transparent !important;
          border: none !important;
          box-shadow: none !important;
        }
      `}</style>
      <div ref={holderRef} className="editorjs-holder prose prose-invert max-w-none" />
      
      {/* Social embed modal */}
      {showSocialModal && socialEmbedUrl && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-75"
          onClick={() => {
            setShowSocialModal(false);
            setIsIframeLoaded(false);
          }}
        >
          <div
            className="relative bg-white rounded-lg max-w-2xl w-full mx-4"
            onClick={(e) => e.stopPropagation()}
          >
            <button
              onClick={() => {
                setShowSocialModal(false);
                setIsIframeLoaded(false);
              }}
              className="absolute top-2 right-2 text-gray-500 hover:text-gray-700 z-10 bg-white rounded-full p-2"
            >
              <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
            <div className="p-4">
              {!isIframeLoaded && (
                <div className="flex items-center justify-center" style={{ minHeight: '500px' }}>
                  <div className="text-gray-500">Loading...</div>
                </div>
              )}
              <iframe
                src={socialEmbedUrl}
                className="w-full"
                style={{
                  border: 'none',
                  borderRadius: '8px',
                  minHeight: '500px',
                  display: isIframeLoaded ? 'block' : 'none',
                }}
                scrolling="no"
                allowTransparency={true}
                onLoad={() => setIsIframeLoaded(true)}
              />
            </div>
          </div>
        </div>
      )}
    </div>
  );
};
